import React, { useCallback, useRef, useState } from "react";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
} from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	rectSortingStrategy,
	arrayMove,
} from "@dnd-kit/sortable";
import { FormLabel } from "newui/components/ui/FormLabel";
import InfoTooltip from "newui/components/InfoTooltip";
import FormikFieldError from "newui/components/ui/FormikFieldError";
import { validateImageFile } from "./components/imageUtils";
import { ImageUploadPlaceholder } from "./components/ImageUploadPlaceholder";
import { SortableImageTile } from "./SortableImageTile";
import { ImagePreviewBaseProps } from "./ImagePreview";

export type MultiImage = File | string;

export interface MultiImagePreviewProps extends ImagePreviewBaseProps {
	images: MultiImage[];
	maxImages?: number;
	onChange: (images: MultiImage[]) => void;
}

/** Generate a stable ID for a given image + index */
const getImageId = (image: MultiImage, index: number): string =>
	image instanceof File
		? `file-${image.name}-${image.size}-${index}`
		: `url-${image}-${index}`;

export const MultiImagePreview: React.FC<MultiImagePreviewProps> = ({
	className = "",
	errorText,
	guideLine,
	hasError = false,
	images,
	label,
	maxImages,
	modalTitleOverride,
	required,
	testId,
	onChange,
}) => {
	const addInputRef = useRef<HTMLInputElement>(null);
	const [overflowWarning, setOverflowWarning] = useState<string | null>(null);
	const canAddMore = maxImages === undefined || images.length < maxImages;

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const processFiles = useCallback(
		(fileList: FileList) => {
			const validFiles: File[] = [];
			const invalidFiles: string[] = [];

			for (let i = 0; i < fileList.length; i++) {
				const result = validateImageFile(fileList[i]);
				if (result.valid) {
					validFiles.push(fileList[i]);
				} else {
					invalidFiles.push(`${fileList[i].name}: ${result.error}`);
				}
			}

			if (validFiles.length === 0 && invalidFiles.length > 0) {
				setOverflowWarning(
					`No files added. ${invalidFiles.join("; ")}`,
				);
				return;
			}

			const slotsAvailable =
				maxImages !== undefined
					? maxImages - images.length
					: validFiles.length;

			const toAdd = validFiles.slice(0, slotsAvailable);
			const skippedCount = validFiles.length - toAdd.length;

			const warnings: string[] = [];
			if (invalidFiles.length > 0) {
				warnings.push(
					`${invalidFiles.length} file(s) skipped (invalid): ${invalidFiles.join("; ")}`,
				);
			}
			if (skippedCount > 0) {
				const skippedNames = validFiles
					.slice(slotsAvailable)
					.map((f) => f.name)
					.join(", ");
				warnings.push(
					`${skippedCount} file(s) not added — maximum of ${maxImages} images reached. Skipped: ${skippedNames}`,
				);
			}

			setOverflowWarning(warnings.length > 0 ? warnings.join(" ") : null);

			if (toAdd.length > 0) {
				onChange([...images, ...toAdd]);
			}
		},
		[images, maxImages, onChange],
	);

	const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			processFiles(e.target.files);
		}
		if (addInputRef.current) {
			addInputRef.current.value = "";
		}
	};

	const handleReplace = (index: number) => (file: File | null) => {
		if (file) {
			const next = [...images];
			next[index] = file;
			onChange(next);
		} else {
			onChange(images.filter((_, i) => i !== index));
		}
	};

	const handleAddTileDrop = (files: FileList) => {
		if (!canAddMore) {
			setOverflowWarning(
				`Cannot add files — maximum of ${maxImages} images already reached.`,
			);
			return;
		}
		processFiles(files);
	};

	// --- Drag-to-reorder handler ---
	const handleSortEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const oldIndex = images.findIndex(
			(img, i) => getImageId(img, i) === active.id,
		);
		const newIndex = images.findIndex(
			(img, i) => getImageId(img, i) === over.id,
		);

		if (oldIndex !== -1 && newIndex !== -1) {
			onChange(arrayMove(images, oldIndex, newIndex));
		}
	};

	const sortableIds = images.map((img, i) => getImageId(img, i));

	return (
		<div className={className} data-testid={testId}>
			{label && (
				<FormLabel label={label} required={required}>
					{guideLine && (
						<div className="inline-block">
							<InfoTooltip tooltipText={guideLine} />
						</div>
					)}
				</FormLabel>
			)}

			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleSortEnd}
			>
				<SortableContext
					items={sortableIds}
					strategy={rectSortingStrategy}
				>
					<div
						className="grid auto-rows-auto gap-3 overflow-y-auto"
						style={{
							gridTemplateColumns:
								"repeat(auto-fill, minmax(220px, 1fr))",
							maxHeight: "340px",
						}}
					>
						{images.map((image, index) => (
							<SortableImageTile
								key={sortableIds[index]}
								id={sortableIds[index]}
								image={image}
								onChange={handleReplace(index)}
								label={
									label
										? `${label} ${index + 1}`
										: `Image ${index + 1}`
								}
								compact
								modalTitleOverride={modalTitleOverride}
							/>
						))}

						{canAddMore && (
							<ImageUploadPlaceholder
								onClick={() => addInputRef.current?.click()}
								onDrop={handleAddTileDrop}
								label={
									maxImages !== undefined
										? `${images.length} / ${maxImages}`
										: undefined
								}
								className="min-h-[140px] max-h-[140px]"
							/>
						)}
					</div>
				</SortableContext>
			</DndContext>

			<input
				ref={addInputRef}
				type="file"
				accept="image/jpeg,image/png"
				multiple
				className="hidden"
				onChange={handleAddFiles}
			/>

			{overflowWarning && (
				<div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
					{overflowWarning}
				</div>
			)}

			<FormikFieldError text={hasError ? errorText : undefined} />
		</div>
	);
};
